import { createSelector } from "reselect";

/*
 * Pure helpers over the `target_loss_ratios` static config.
 *
 * Config shape (ratios are decimals, so 0.6 is 60%):
 *   { rows: [{ model: "WORKERS_COMP", layer: "Primary", lossRatio: 0.6 },
 *            { model: "WORKERS_COMP", layer: "Excess", lossRatio: 0.5 }, ...] }
 *
 * `model` is the key of a pricing model (Inputs > Program > Model, listed in
 * `classifications.models`); the display name is looked up from that list at
 * render time, so the config never repeats it. None of these helpers depend on
 * React or the Redux store, so the Rate Adequacy tab can reuse them with the
 * prior submission's model.
 */

const normalise = (value) =>
  value == null ? "" : String(value).trim().toLowerCase();

const getRows = (configData) => configData?.rows;

export const selectTargetLossRatioRows = createSelector([getRows], (rows) =>
  Array.isArray(rows)
    ? rows.filter((row) => row != null && typeof row === "object")
    : []
);

// The rows for one model key; every row when no model is given.
export const filterTargetLossRatios = (rows, modelKey) => {
  const safeRows = Array.isArray(rows)
    ? rows.filter((row) => row != null && typeof row === "object")
    : [];
  const key = normalise(modelKey);
  if (key === "") {
    return safeRows;
  }
  return safeRows.filter((row) => normalise(row.model) === key);
};

// The rows to display for a selected model: its own rows when it has any,
// otherwise the whole schedule, so an unmatched or unset model still shows
// the targets rather than an empty card.
export const selectDisplayedTargetLossRatios = (rows, modelKey) => {
  const matched = filterTargetLossRatios(rows, modelKey);
  return matched.length > 0 ? matched : filterTargetLossRatios(rows, null);
};

// A layer reads as excess when it is named that way ("Excess", "XS Cessions",
// "xs"); anything else, including a missing layer, is treated as primary.
export const isExcessLayer = (layer) => {
  const value = normalise(layer);
  return value.startsWith("excess") || value.startsWith("xs");
};

// The ratio for the first row of the wanted layer kind among `rows`, which
// callers have already narrowed to one model.
export const pickTargetLossRatio = (rows, isExcess = false) => {
  const row = (Array.isArray(rows) ? rows : []).find(
    (candidate) =>
      candidate != null && isExcessLayer(candidate.layer) === !!isExcess
  );
  const value = row?.lossRatio;
  return typeof value === "number" && Number.isFinite(value) ? value : null;
};
